<template>
	<page-container :page="page">
		<heading-image
			v-if="page.headingImage"
			:heading-image="{
				image: page.headingImage,
				imageAlt: page.headingImageAlt,
				imageWebp: page.headingImageWebp,
				imagePortrait: page.headingImagePortrait,
				imagePortraitWebp: page.headingImagePortraitWebp,
			}"
		/>
		<breadcrumbs :breadcrumbs="page.breadcrumbs" :class="page.headingImage ? '' : 'no-slider'" />

		<main-content :title="page.title || page.header" :content="page.content" :subtitle="page.subtitle" />

		<section class="main-content gallery-section background-light">
			<div class="row filter-buttons align-center">
				<div class="columns column12">
					<div class="flex-row gallery-nav">
						<div
							class="button-toggle"
							:class="{ active: filter === 'showAll' }"
							name="showall"
							@click="filter = 'showAll'"
						>
							{{ $t('showAll') }}
						</div>
						<div
							v-for="category in categories"
							:key="category.ID"
							class="button-toggle"
							:name="category.ID"
							:class="{ active: filter === category.ID }"
							@click="filter = category.ID"
						>
							{{ category.header }}
						</div>
					</div>
				</div>
			</div>
			<div class="row items flex-row">
				<div class="columns column12">
					<div class="gallery-wrapper flex-row">
						<masonry :cols="{ default: 3, 1000: 2 }" :gutter="15">
							<div
								v-for="item in gallery"
								:key="item.ID"
								class="filter-item"
								:class="{ active: filter === item.galleryCategorie.ID || filter === 'showAll' }"
							>
								<a
									v-if="item.embedCode"
									:data-src="item.embedCode"
									class="item"
									data-fancybox="gallery"
									:data-caption="item.imageAlt"
									rel="Gallery"
									:title="item.imageAlt"
								>
									<div class="video-play" />
									<picture>
										<source v-if="item.imagesWebp" :srcset="item.imagesWebp[1]" type="image/webp" />
										<source :srcset="item.images[1]" />
										<img :src="item.images[1]" :alt="item.imageAlt" loading="lazy" />
									</picture>
								</a>
								<a
									v-else-if="item.imagePortrait"
									:data-src="item.imagePortrait"
									class="item"
									data-fancybox="gallery"
									:data-caption="item.imagePortraitAlt"
									rel="Gallery"
									:title="item.imagePortraitAlt"
								>
									<picture>
										<source
											v-if="item.imagesWebp"
											:srcset="item.imagePortraitWebp"
											type="image/webp"
										/>
										<source :srcset="item.imagePortrait" />
										<img :src="item.image" :alt="item.imagePortraitAlt" loading="lazy" />
									</picture>
								</a>
								<a
									v-else
									:data-src="item.image"
									class="item"
									data-fancybox="gallery"
									:data-caption="item.imageAlt"
									rel="Gallery"
									:title="item.imageAlt"
								>
									<picture>
										<source v-if="item.imagesWebp" :srcset="item.imageWebp" type="image/webp" />
										<source :srcset="item.image" />
										<img :src="item.image" :alt="item.imageAlt" loading="lazy" />
									</picture>
								</a>
							</div>
						</masonry>
					</div>
				</div>
			</div>
		</section>

		<contentblocks
			v-if="page && page.contentblocks && page.contentblocks.length"
			:items="page.contentblocks"
			:content-blocks-content="page.sectionContentblocksContent"
			:content-blocks-subtitle="page.sectionContentblocksTitle"
		/>
		<frequently-asked-questions
			v-if="page && page.frequentlyAskedQuestion"
			:header="page.frequentlyAskedQuestion.header"
			:items="page.frequentlyAskedQuestion.faqitems"
		/>
		<pre-footer
			v-if="page && page.footerSection && page.footerSection.length"
			:footer-section="page.footerSection[0]"
		/>
		<newsletter
			v-if="page && page.showNewsletter"
			:header="defaults[locale].newsletter.title"
			:content="defaults[locale].newsletter.content"
			:button="defaults[locale].newsletter.button"
		/>
	</page-container>
</template>

<script setup>
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

const { page, fetchPage } = usePage();
const { defaults, fetchDefaults } = useDefaults();
const { locale } = useI18n();

await fetchPage();
await fetchDefaults();

const filter = ref('showAll');

const { data: gallery } = await useWebsiteFetch('gallery', {
	query: { language: locale.value },
	key: `${locale.value}/gallery`,
});

let categories = gallery.value.map((item) => item.galleryCategorie);
categories = [...new Map(categories.map((item) => [item.ID, item])).values()].sort((a, b) => a.position - b.position);

onMounted(() => {
	Fancybox.bind('[data-fancybox]', {});
});
</script>

<style lang="scss" scoped>
.gallery .gallery-section {
	margin: 0;
}

.item {
	cursor: pointer;
}

.video-play {
	background: url('~/assets/images/video-play-button.png') no-repeat center center;
	background-size: 40px;
	position: absolute;
	top: 50%;
	left: 50%;
	z-index: 3;
	width: 40px;
	height: 40px;
	margin: -20px 0 0 -20px;
	cursor: pointer;
	transition: all 0.3s ease-in-out;
}

.gallery-nav {
	background: #fff;
	flex-wrap: wrap;
	justify-content: center;
	align-items: stretch;

	div {
		flex-grow: 1;
	}

	.button-toggle {
		padding: 25px;
		text-align: left;
		cursor: pointer;
		transition: all 0.3s ease-in-out;

		&.active,
		&:hover {
			background: $cta-color;
			color: #fff;
		}
	}
}

.filter-item {
	display: none;

	&.active {
		display: block;
	}
}

.gallery-wrapper {
	background: #fff;
	padding: 15px;
}

.gallery-section {
	margin: 10px auto 0;

	.row {
		display: flex;
		flex-wrap: wrap;
		place-content: space-between center;
	}

	.columns {
		margin: 15px 0;
	}

	img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}
}

@media (max-width: 720px) {
	.gallery-nav {
		background: none;

		div {
			width: 48%;
			margin: 1%;
			background: #fff;
		}
	}
}
</style>
